import styles from './customerQuickForm.module.css'
import { Data2, DataTypeForm2, FinalCustomerName, Labels, validateEmail, validateIdentifier, validatePhone, validateTaxId } from "../../../../../features/data-types"
import { FieldType, FieldValue, FormField, NumberField, O, option, useLocalStorageActions } from "../../../../../features/ui"
import { useActions } from "./actions"
import { Customer, CustomerCategory } from "../../../state/types"
import { IdentifierField } from "../identifier-field/identifierField"
import { useMemo, useState } from "react"
import { IdentifierType } from "../../../../system"
import { TableName } from "../../../../../tables"
import { useCustomerTypeLoaders } from "../../../loaders/customerTypeLoaders"
import { VatCondition } from "../../../../administration"

type Props = {
    onSubmit: (customer: Customer) => void
    onCancel?: () => void
}

export const CustomerQuickForm = (props: Props) => {
    const { onSubmit, onCancel } = props
    
    const { getNewCustomerData, removeNewCustomerData } = useLocalStorageActions()
    const { customerData, creditAccountData } = useMemo(() => getNewCustomerData(), [])
    
    const [identifierType, setIdentifierType] = useState<O<IdentifierType>>(customerData?.identifierType || IdentifierType.PERSONAL_ID)
    const [identifier, setIdentifier] = useState<O<number>>(customerData?.identifier)
    const [identifierValidation, setIdentifierValidation] = useState<string>()
    const [creditAccount, setCreditAccount] = useState<boolean>(!!creditAccountData)
    const [creditLimit, setCreditLimit] = useState<O<number>>(creditAccountData?.creditLimit)
    const [creditDays, setCreditDays] = useState<O<number>>(creditAccountData?.creditDays)

    const customerTypeLoaders = useCustomerTypeLoaders()

    const { submitCustomer } = useActions()

    const onChangeIdentifierType = (value?: IdentifierType) => {
        setIdentifierType(value as O<IdentifierType>)
        setIdentifierValidation(undefined)
    }

    const onChangeIdentifier = (value?: number) => {
        setIdentifier(value as O<number>)
        setIdentifierValidation(undefined)
    }

    const onChangeCreditAccount = (value?: FieldValue) => {
        const newCreditAccount = !!value
        setCreditAccount(newCreditAccount)
        if (newCreditAccount) {
            setCreditDays(30)
        } else {
            setCreditLimit(undefined)
            setCreditDays(undefined)
        }
    }

    const onChangeCreditLimit = (value?: FieldValue) => setCreditLimit(value as O<number>)
    
    const onChangeCreditDays = (value?: FieldValue) => setCreditDays(value as O<number>)

    const fields: FormField[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre / Razón Social',
            defaultValue: customerData?.name
        },
        {
            name: 'category',
            type: FieldType.SELECT,
            label: 'Cateogría',
            defaultValue: customerData?.category || CustomerCategory.NATURAL,
            options: Object.values(CustomerCategory).map(category => option(category, Labels.customerCategoryLabel(category))),
            narrow: true
        },
        IdentifierField({
            defaultIdentifierType: identifierType,
            defaultIdentifier: identifier,
            validation: identifierValidation,
            onChangeIdentifierType,
            onChangeIdentifier
        }),
        {
            name: 'customerTypeId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMER_TYPES,
            label: 'Lista de Precios',
            defaultValue: customerData?.customerTypeId || customerTypeLoaders().findCustomerType(FinalCustomerName, 'name')?.id
        },
        {
            name: 'vatCondition',
            type: FieldType.SELECT,
            label: 'Condición frente al IVA',
            defaultValue: customerData?.vatCondition || VatCondition.FINAL_CONSUMER,
            options: Object.values(VatCondition).map(condition => option(condition, Labels.vatConditionLabel(condition)))
        },
        {
            name: 'phone',
            type: FieldType.NUMBER,
            label: 'Teléfono',
            optional: true,
            validate: validatePhone
        },
        {
            name: 'email',
            type: FieldType.TEXT,
            label: 'Email',
            optional: true,
            validate: validateEmail
        },
        {
            name: 'creditAccount',
            type: FieldType.BOOLEAN,
            label: 'Cuenta Corriente',
            value: creditAccount,
            onChange: onChangeCreditAccount
        }
    ]
    if (creditAccount) {
        const creditLimitField = {
            name: 'creditLimit',
            type: FieldType.PRICE,
            label: 'Crédito Otorgado',
            value: creditLimit,
            onChange: onChangeCreditLimit
        }
        const creditDaysField = {
            name: 'creditDays',
            type: FieldType.NUMBER,
            label: 'Días de Crédito',
            render: () => <>
                <NumberField
                    id="creditDays"
                    value={creditDays}
                    integer
                    className={styles.creditDays}
                    onChange={onChangeCreditDays}
                />
                <span>días</span>
            </>
        }
        fields.push(creditLimitField, creditDaysField)
    }
    
    const validate = (): boolean => {
        const isPersonalId = identifierType === IdentifierType.PERSONAL_ID
        const data = isPersonalId ? { identifier } : { taxId: identifier }
        const validationMessage = isPersonalId ? validateIdentifier(data) : validateTaxId(data)
        validationMessage && setIdentifierValidation(validationMessage)
        return !validationMessage
    }

    const handleSubmit = async (data: Data2) => {
        if (validate()) {
            const savedCustomer = await submitCustomer(data, identifierType!, identifier!, creditLimit, creditDays)
            savedCustomer && onSubmit(savedCustomer)
        }
    }

    const handleCancel = () => {
        removeNewCustomerData()
        onCancel?.()
    }

    return (
        <DataTypeForm2
            formId="customer-quick-form"
            fields={fields}
            createMode={true}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
        />
    )
}
